// API Service for Weather Data Platform Frontend
// Handles all communication with the backend API

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const REQUEST_TIMEOUT = 15000; // 15 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second base delay

class ApiService {
  constructor() {
    this.isOnline = true;
    this.onStatusChange = null;
  }

  setStatusCallback(callback) {
    this.onStatusChange = callback;
  }

  updateStatus(online) {
    if (this.isOnline !== online) {
      this.isOnline = online;
      if (this.onStatusChange) this.onStatusChange(online);
    }
  }

  async fetchWithTimeout(url, config, timeout = REQUEST_TIMEOUT) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, { ...config, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  async request(endpoint, options = {}, retries = MAX_RETRIES) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    let lastError;
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const response = await this.fetchWithTimeout(url, config);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        this.updateStatus(true);
        return await response.json();
      } catch (error) {
        lastError = error;
        console.warn(`API attempt ${attempt + 1}/${retries} failed: ${endpoint}`, error.message);
        
        if (attempt < retries - 1) {
          await new Promise(r => setTimeout(r, RETRY_DELAY * (attempt + 1)));
        }
      }
    }
    
    this.updateStatus(false);
    console.error(`API request failed after ${retries} attempts: ${endpoint}`, lastError);
    throw lastError;
  }

  async getStations() {
    return this.request('/api/stations');
  }

  async getLatestReading(stationId) {
    return this.request(`/api/weather-data/latest/${stationId}`);
  }

  async validateCSV(file) {
    const formData = new FormData();
    formData.append('file', file);

    return fetch(`${API_BASE_URL}/api/import/validate`, {
      method: 'POST',
      body: formData,
    }).then(response => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || `Validation failed: ${response.statusText}`);
        });
      }
      return response.json();
    });
  }

  async uploadCSV(file, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText);
            resolve(result);
          } catch (e) {
            reject(new Error('Invalid response format'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.error || `Upload failed: ${xhr.statusText}`));
          } catch (e) {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed: Network error'));
      });

      xhr.open('POST', `${API_BASE_URL}/api/import/csv`);
      xhr.send(formData);
    });
  }

  async getImportBatches(limit = 10) {
    return this.request(`/api/import/batches?limit=${limit}`);
  }

  async getDailySummary(months = 7, year = null, month = null) {
    if (year && month && months > 1) {
      return this.request(`/api/weather-data/daily-summary?months=${months}&year=${year}&month=${month}`);
    } else if (year && month) {
      return this.request(`/api/weather-data/daily-summary?months=1&year=${year}&month=${month}`);
    }
    return this.request(`/api/weather-data/daily-summary?months=${months}`);
  }

  async getWindDirection(months = 1, year = null, month = null) {
    if (year && month && months > 1) {
      return this.request(`/api/weather-data/wind-direction?months=${months}&year=${year}&month=${month}`);
    } else if (year && month) {
      return this.request(`/api/weather-data/wind-direction?months=1&year=${year}&month=${month}`);
    }
    return this.request(`/api/weather-data/wind-direction?months=${months}`);
  }

  async getMpptData(months = 1, year = null, month = null) {
    if (year && month && months > 1) {
      return this.request(`/api/weather-data/mppt-data?months=${months}&year=${year}&month=${month}`);
    } else if (year && month) {
      return this.request(`/api/weather-data/mppt-data?months=1&year=${year}&month=${month}`);
    }
    return this.request(`/api/weather-data/mppt-data?months=${months}`);
  }

  async getLatestMppt() {
    return this.request('/api/weather-data/latest-mppt');
  }

  async getLatestSmartshunt() {
    return this.request('/api/weather-data/latest-smartshunt');
  }

  async getSmartshuntData(months = 1, year = null, month = null) {
    if (month && year) {
      return this.request(`/api/weather-data/smartshunt-data?months=${months}&year=${year}&month=${month}`);
    } else if (year && month) {
      return this.request(`/api/weather-data/smartshunt-data?months=1&year=${year}&month=${month}`);
    }
    return this.request(`/api/weather-data/smartshunt-data?months=${months}`);
  }

  async getMpptBatteryHourly(startDate, endDate) {
    return this.request(`/api/weather-data/mppt-battery-hourly?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
  }

  // Network Discovery
  async getNetworkDevices(status = null, location = null) {
    let query = '';
    const params = [];
    if (status) params.push(`status=${status}`);
    if (location) params.push(`location=${location}`);
    if (params.length) query = '?' + params.join('&');
    return this.request(`/api/iot/network-devices${query}`);
  }

  async approveNetworkDevice(id, deviceType, friendlyName) {
    return this.request(`/api/iot/network-devices/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ device_type: deviceType, friendly_name: friendlyName }),
    });
  }

  async ignoreNetworkDevice(id) {
    return this.request(`/api/iot/network-devices/${id}/ignore`, {
      method: 'POST',
    });
  }

  async deleteNetworkDevice(id) {
    return this.request(`/api/iot/network-devices/${id}`, {
      method: 'DELETE',
    });
  }

  // Hydro Usage
  async getHydroHourlyStats(location = null, startDate = null, endDate = null) {
    const params = [];
    if (location) params.push(`location=${encodeURIComponent(location)}`);
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    return this.request(`/api/weather-data/hydro-hourly-stats${query}`);
  }

  async getHydroDaily(location = null, startDate = null, endDate = null) {
    const params = [];
    if (location) params.push(`location=${encodeURIComponent(location)}`);
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    return this.request(`/api/weather-data/hydro-daily${query}`);
  }

  // AC Charger Events
  async getAcChargerEvents(months = 1, year = null, month = null) {
    if (year && month && months > 1) {
      return this.request(`/api/weather-data/ac-charger-events?months=${months}&year=${year}&month=${month}`);
    } else if (year && month) {
      return this.request(`/api/weather-data/ac-charger-events?months=1&year=${year}&month=${month}`);
    }
    return this.request(`/api/weather-data/ac-charger-events?months=${months}`);
  }

  // System Alerts
  async getAlerts() {
    return this.request('/api/alerts');
  }

  async acknowledgeAlert(id) {
    return this.request(`/api/alerts/${id}/acknowledge`, {
      method: 'POST',
    });
  }
}

export const apiService = new ApiService();
export default apiService;
